  import React from "react";
import { handleApiResponse } from 'utilities/apiUtils';
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import Stepper from "../../../components/Stepper";
import type { Certificate, PolicyItem } from "../../dashboard/types/dashboard";
import '../../../styles/pages.css';
import '../../../styles/panels.css';
import Step1, { Step1Ref } from "./Step1";
import Step2, { Step2Ref } from "./Step2";
import Step3 from "./Step3";
import Step4 from "./Step4";
import Step5 from "./Step5";
import Step7 from "./Step7";
import Step6MassSection from "./Step6";

export type SendCertificatePageProps = {
  onBack: () => void;
  certificates: Certificate[];
  rawMasterCertificates?: any[];
  syncedPolicies?: PolicyItem[];
  insuredDatabaseId?: string;
  insuredEmail?: string;
  initialEmail?: string;
  initialContactName?: string;
  initialCompanyName?: string;
  initialMasterCertId?: string;
  masterCertsLoading?: boolean;
};

export default function SendCertificatePage({
  onBack,
  certificates,
  rawMasterCertificates = [],
  syncedPolicies = [],
  insuredDatabaseId,
  insuredEmail,
  initialEmail = "",
  initialContactName = "",
  initialCompanyName = "",
  initialMasterCertId,
  masterCertsLoading = false,
}: SendCertificatePageProps) {
    // Mass Action: selected sent certificate IDs
    const [selectedSentCertIds, setSelectedSentCertIds] = React.useState<string[]>([]);
  const step1Ref = React.useRef<Step1Ref>(null);
  const step2Ref = React.useRef<Step2Ref>(null);
  const toast = React.useRef<any>(null);
  const sessionId = React.useRef<string>(crypto.randomUUID());
  const [uploadedFiles, setUploadedFiles] = React.useState<Array<{ name: string; fileId: string; downloadUrl?: string }>>([]);
  const [isFileUploading, setIsFileUploading] = React.useState(false);
  const [combinePdf, setCombinePdf] = React.useState(false);
  const [showDescriptionInAcord101, setShowDescriptionInAcord101] = React.useState(false);
  const [descTemplate, setDescTemplate] = React.useState("");
  const [descText, setDescText] = React.useState("");
  const [descTemplateOptions, setDescTemplateOptions] = React.useState<{ label: string; value: string; body?: string }[]>([]);
  const attachedDescription =
    "This is the attached description for the certificate.";

  const [emailSubject, setEmailSubject] = React.useState("");
  const [emailTemplate, setEmailTemplate] = React.useState("");
  const [emailBody, setEmailBody] = React.useState("");
  const [emailTemplateOptions, setEmailTemplateOptions] = React.useState<{ label: string; value: string; subject?: string; body?: string }[]>([]);
  const emailTemplatesLoaded = React.useRef(false);
  const descTemplatesLoaded = React.useRef(false);
  const [emailBodyLoading, setEmailBodyLoading] = React.useState(false);
  const [descBodyLoading, setDescBodyLoading] = React.useState(false);
  const [descTemplatesLoading, setDescTemplatesLoading] = React.useState(false);
  const [emailTemplatesLoading, setEmailTemplatesLoading] = React.useState(false);

  const fetchTemplatesByCategory = React.useCallback(async (category: 'Email' | 'Certificates', search?: string) => {
    if (category === 'Certificates') setDescTemplatesLoading(true);
    else setEmailTemplatesLoading(true);
    try {
      const ncToken = localStorage.getItem('NCToken') || '';
      const authToken = localStorage.getItem('igtoken') || '';
      const params = new URLSearchParams({ top: '100', category });
      if (search?.trim()) params.set('search', search.trim());
      const url = `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/certificate-templates?${params.toString()}`;
      const response = await fetch(url, {
        headers: {
          'NCToken': ncToken,
          'Accept': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
      });
      await handleApiResponse(response);
      const data = await response.json();
      if (!data.value) return;
      if (category === 'Email') {
        setEmailTemplateOptions(
          data.value.map((item: any) => ({
            label: item.name,
            value: item.id,
            subject: item.subject,
            body: item.body,
          }))
        );
      } else {
        setDescTemplateOptions(
          data.value.map((item: any) => ({
            label: item.name,
            value: item.id,
            body: item.body,
          }))
        );
      }
    } catch {
      // non-critical
    } finally {
      if (category === 'Certificates') setDescTemplatesLoading(false);
      else setEmailTemplatesLoading(false);
    }
  }, []);

  const descSearchTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleDescTemplateSearch = React.useCallback((query: string) => {
    if (descSearchTimerRef.current) clearTimeout(descSearchTimerRef.current);
    descSearchTimerRef.current = setTimeout(() => {
      fetchTemplatesByCategory('Certificates', query);
    }, 300);
  }, [fetchTemplatesByCategory]);

  const emailSearchTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleEmailTemplateSearch = React.useCallback((query: string) => {
    if (emailSearchTimerRef.current) clearTimeout(emailSearchTimerRef.current);
    emailSearchTimerRef.current = setTimeout(() => {
      fetchTemplatesByCategory('Email', query);
    }, 300);
  }, [fetchTemplatesByCategory]);

  React.useEffect(() => {
    if (initialEmail) setEmail(initialEmail);
    if (initialContactName) setContactName(initialContactName);
    if (initialCompanyName) setCompanyName(initialCompanyName);
  }, [initialEmail, initialContactName, initialCompanyName]);
  const fillTemplate = (template: string): string => {
    if (!template) return template;

    const insuredName = insuredCommercialName || `${insuredFirstName} ${insuredLastName}`.trim();
    const primaryPolicy = syncedPolicies[0];

    const replacements: Record<string, string | undefined> = {
      'Insured.StateName': insuredState || undefined,
      'Insured.Name': insuredName || undefined,
      'Policy.Number': primaryPolicy?.policyNumber,
      'Policy.EffectiveDate': primaryPolicy?.effectiveDateLabel,
      'Policy.ExpirationDate': primaryPolicy?.expirationDateLabel,
      'Policy.StatusExcel': primaryPolicy?.status,
    };

    return template.replace(/\{([^}]+)\}/g, (match, key) => {
      const replacement = replacements[key];
      return typeof replacement === 'string' && replacement.length > 0
        ? replacement
        : match;
    });
  };

  const replacePlaceholders = React.useCallback(async (text: string): Promise<string> => {
    if (!insuredDatabaseId || !text) return text;
    try {
      const ncToken = localStorage.getItem('NCToken') || '';
      const authToken = localStorage.getItem('igtoken') || '';
      const response = await fetch(`${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/replace-placeholders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${authToken}`,
          'NCToken': ncToken,
        },
        body: JSON.stringify({ text, truckingCompanyId: insuredDatabaseId }),
      });
      if (!response.ok) return fillTemplate(text);
      const data = await response.json();
      return data.text ?? fillTemplate(text);
    } catch {
      return fillTemplate(text);
    }
  }, [insuredDatabaseId]);

  // When template changes, set the selected template id and prefill subject/body
  const handleSetEmailTemplate = async (id: string) => {
    setEmailTemplate(id);
    const tmpl = emailTemplateOptions.find(t => t.value === id);
    if (tmpl?.subject) {
      setEmailSubject(tmpl.subject);
      setIsEmailSubjectDirty(true);
    }
    if (tmpl?.body) {
      setEmailBodyLoading(true);
      try {
        const resolved = await replacePlaceholders(tmpl.body);
        setEmailBody(prev => prev ? `${prev}\n${resolved}` : resolved);
      } finally {
        setEmailBodyLoading(false);
      }
    }
  };

  const handleSetDescTemplate = async (id: string) => {
    setDescTemplate(id);
    const tmpl = descTemplateOptions.find(t => t.value === id);
    if (tmpl?.body) {
      setDescBodyLoading(true);
      try {
        const resolved = await replacePlaceholders(tmpl.body);
        setDescText(prev => prev ? `${prev}\n${resolved}` : resolved);
      } finally {
        setDescBodyLoading(false);
      }
    }
  };
  const masterCertificateOptions = certificates.map((c: Certificate) => ({
    label: c.title,
    value: c.id,
  }));
  const [selectedMasterCertificates, setSelectedMasterCertificates] =
    React.useState<string[]>(initialMasterCertId ? [initialMasterCertId] : []);

  const selectedMasterCertPolicyIds = React.useMemo<string[]>(() => {
    const certId = selectedMasterCertificates[0];
    if (!certId || !rawMasterCertificates.length) return [];
    const mc = rawMasterCertificates.find((m: any) => m.databaseId === certId);
    return mc?.policies?.map((p: any) => p.databaseId).filter(Boolean) ?? [];
  }, [selectedMasterCertificates, rawMasterCertificates]);

  const [selectedVehicles, setSelectedVehicles] = React.useState<string[]>([]);
  const [selectedDrivers, setSelectedDrivers] = React.useState<string[]>([]);
  const [selectedEquipment, setSelectedEquipment] = React.useState<string[]>(
    [],
  );
  const [selectedProperties, setSelectedProperties] = React.useState<string[]>(
    [],
  );
  const [step, setStep] = React.useState(0);

  // Lazy-load Certificates (desc of operations) templates only when user reaches step 2
  React.useEffect(() => {
    if (step === 2 && !descTemplatesLoaded.current) {
      descTemplatesLoaded.current = true;
      fetchTemplatesByCategory('Certificates');
    }
  }, [step, fetchTemplatesByCategory]);

  // Lazy-load Email templates only when user reaches step 3
  React.useEffect(() => {
    if (step === 3 && !emailTemplatesLoaded.current) {
      emailTemplatesLoaded.current = true;
      fetchTemplatesByCategory('Email');
    }
  }, [step, fetchTemplatesByCategory]);

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [warning, setWarning] = React.useState<string | null>(null);

  const [email, setEmail] = React.useState("");
  const [isEmailValid, setIsEmailValid] = React.useState(false);
  const [contactName, setContactName] = React.useState("");
  const [companyName, setCompanyName] = React.useState("");
  const [sendToEmail, setSendToEmail] = React.useState(true);
  const [ccToMe, setCcToMe] = React.useState(false);
  const [copyToInsuredEmail, setCopyToInsuredEmail] = React.useState<string[]>([]);
  const [sendToInsuredEmail, setSendToInsuredEmail] = React.useState(false);
  const [sendToInsuredFax, setSendToInsuredFax] = React.useState(false);
  const [sendToFax, setSendToFax] = React.useState(false);
  const [certificateHolder, setCertificateHolder] = React.useState<{ label: string; value: string }[]>([]);
  const [fax, setFax] = React.useState("");
  const [insuredFax, setInsuredFax] = React.useState("");
  const [coverPageOptions, setCoverPageOptions] = React.useState<string | null>(null);
  const [insuredFirstName, setInsuredFirstName] = React.useState("");
  const [insuredLastName, setInsuredLastName] = React.useState("");
  const [insuredCommercialName, setInsuredCommercialName] = React.useState("");
  const [insuredState, setInsuredState] = React.useState("");
  const [isEmailSubjectDirty, setIsEmailSubjectDirty] = React.useState(false);

  const [selectedCertificateIds, setSelectedCertificateIds] = React.useState<
    string[]
  >([]);
  const [message, setMessage] = React.useState("");
  const allSteps = [
    { label: "General" },
    { label: "Insured items" },
    { label: "Description of operations" },
    { label: "Email" },
    { label: "Attach files" },
    { label: "Review & send" },
  ];
  // Email step (index 3) is shown when any email-related option is checked
  const showEmailStep = sendToEmail || ccToMe || sendToInsuredEmail;
  const steps = showEmailStep ? allSteps : allSteps.filter((_, i) => i !== 3);
  // Map internal step index → visual stepper index
  const visualStep = showEmailStep ? step : (step < 3 ? step : step - 1);


  const reset = () => {
    setStep(0);
    setEmail("");
    setSelectedCertificateIds([]);
    setMessage("");
    setCertificateHolder([]);
    setEmailSubject("");
    setIsEmailSubjectDirty(false);
    setUploadedFiles([]);
  };

  const canContinueFromRecipient = isEmailValid;

  const selectedCertificates = certificates.filter((c) => selectedCertificateIds.includes(c.id));

  const handleEmailSubjectChange = (value: string) => {
    setEmailSubject(value);
    setIsEmailSubjectDirty(true);
  };

  React.useEffect(() => {
    if (isEmailSubjectDirty) return;

    const insuredPart = insuredCommercialName
      ? `Insured: '${insuredCommercialName}'`
      : "";

    let holderPart = "";
    const firstHolder = certificateHolder[0] as any;
    if (firstHolder) {
      const raw = firstHolder._raw ?? null;
      const holderName = raw?.name || firstHolder.label;

      let address = "";
      if (raw?.addressLine1) {
        address = raw.addressLine1;
      } else if (raw?.city || raw?.stateName || raw?.zipCode) {
        const parts = [raw.city, raw.stateName, raw.zipCode].filter(Boolean);
        address = parts.join(" ");
      }

      const holderDisplay = address ? `${holderName} - ${address}` : holderName;
      holderPart = `Certificate Holder: '${holderDisplay}'`;
    }

    const pieces = [insuredPart, holderPart].filter(Boolean).join(", ");
    const subject = pieces ? `Certificate for ${pieces}` : "Certificate";

    setEmailSubject(subject);
  }, [insuredCommercialName, certificateHolder, isEmailSubjectDirty]);

  const handleSendCertificate = async (scheduledDate?: Date) => {
    setLoading(true);
    setError(null);
    try {
      if (sendToEmail && !email?.trim()) {
        setWarning("Please enter a valid recipient email address before sending.");
        setLoading(false);
        return;
      }
      setWarning(null);
      // Ensure CertificateId is set
      // Use selected master certificate's databaseId as CertificateId
      const certificateId = selectedMasterCertificates[0] || "";
      if (!certificateId) {
        setError("Please select a certificate to send.");
        setLoading(false);
        return;
      }
      // Build comma-separated emails: recipient(s) (if sendToEmail) + insured emails + CC to me
      const emailsList: string[] = [];
      if (sendToEmail && email) {
        email.split(',').map(e => e.trim()).filter(Boolean).forEach(e => { if (!emailsList.includes(e)) emailsList.push(e); });
      }
      if (sendToInsuredEmail) {
        copyToInsuredEmail.forEach(e => { if (e && !emailsList.includes(e)) emailsList.push(e); });
      }
      if (ccToMe) {
        const myEmail = localStorage.getItem('workEmail') || '';
        if (myEmail && !emailsList.includes(myEmail)) emailsList.push(myEmail);
      }

      const payload: any = {
        emails: emailsList.join(',') || null,
        fax: fax || null,
        sendCopyToInsuredEmail: sendToInsuredEmail && copyToInsuredEmail.length > 0,
        sendCopyToInsuredFax: sendToInsuredFax && !!insuredFax,
        sendCCToMe: ccToMe,
        certificateId,
        insuredDatabaseId: insuredDatabaseId || "",
        insuredFirstName: insuredFirstName || "",
        insuredLastName: insuredLastName || "",
        insuredName: insuredCommercialName || "",
        insuredEmail: insuredEmail || "",
        certificateHolder: certificateHolder.map(h => ({ id: h.value })),
        additionalInsuredId: (() => {
          for (const h of certificateHolder) {
            const raw = (h as any)._raw;
            const match = raw?.additionalInsuredDetails?.find(
              (ai: any) => ai.truckingCompanyId === insuredDatabaseId
            );
            if (match) return match.dbId;
          }
          return null;
        })(),
        historicalCertificateHolderIds: selectedSentCertIds ?? [],
        vehicleIds: selectedVehicles.map(id => ({ id })),
        driverIds: selectedDrivers.map(id => ({ id })),
        equipmentIds: selectedEquipment.map(id => ({ id })),
        propertyIds: selectedProperties.map(id => ({ id })),
        emailSubject,
        emailBody: emailBody.replace(/\n/g, '<br>'),
        description: descText,
        showDescriptionInAcord101,
        mergedAllPDFs: combinePdf,
        filesDatabaseId: uploadedFiles.map(f => f.fileId),
        isAttachmentsRequired: uploadedFiles.length > 0,
        coverPageOptions: coverPageOptions || null,
        scheduledDate: scheduledDate
          ? scheduledDate.toISOString().replace('T', ' ').slice(0, 19)
          : null,
        sessionId: sessionId.current,
      };

      const ncToken = localStorage.getItem('NCToken') || '';
      const authToken = localStorage.getItem('igtoken') || '';
      const response = await fetch(`${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/send-certificate`, {
        method: 'POST',
        headers: {
          'NCToken': ncToken,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(payload),
      });
      await handleApiResponse(response);
      toast.current?.show({ severity: 'success', summary: 'Success', detail: scheduledDate ? 'Certificate scheduled successfully!' : 'Certificate sent successfully!', life: 2000 });
      setTimeout(() => {
        reset();
        onBack();
      }, 500);
    } catch (err: any) {
      const raw: string = err?.message || '';
      setError(
        raw === '__server_error__' ? '__server_error__' :
        raw === '__bad_request__' ? '__bad_request__' :
        raw === 'Unauthorized' ? 'Unauthorized' :
        raw || '__api_error__'
      );
    } finally {
      setLoading(false);
    }
  };

  const renderFooter = () => (
    <div className="sendCertDialog__footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Button
        label="Close"
        className="p-button-text appButton--secondary"
        onClick={() => {
          reset();
          onBack();
        }}
      />
      <div style={{ display: 'flex', gap: '8px' }}>
        {step > 0 && (
          <Button
            label="Back"
            className="p-button-text appButton--secondary"
            onClick={() => {
              if (step === 4 && !showEmailStep) setStep(2);
              else setStep((s) => s - 1);
            }}
          />
        )}
        {step < 5 && (
          <Button
            label="Continue"
            className="appButton--primary"
            disabled={(step === 0 && masterCertsLoading) || (step === 2 && descBodyLoading) || (step === 3 && emailBodyLoading) || (step === 4 && isFileUploading)}
            loading={(step === 0 && masterCertsLoading) || (step === 2 && descBodyLoading) || (step === 3 && emailBodyLoading) || (step === 4 && isFileUploading)}
            onClick={() => {
              if (step === 0) {
                if (step1Ref.current?.validate()) setStep((s) => s + 1);
              } else if (step === 1) {
                if (step2Ref.current?.validate()) setStep((s) => s + 1);
              } else if (step === 2 && !showEmailStep) {
                setStep(4); // skip Email step
              } else {
                setStep((s) => s + 1);
              }
            }}
          />
        )}
      </div>
    </div>
  );

  return (
    <div className="sendCertPage__container">
      <Toast ref={toast} />
      <div className="appDialog__header" style={{ marginBottom: 10 }}>
        <div className="appDialog__title" style={{ color: '#0c0c0c', fontWeight: 600 }}>Send Certificate</div>
        <Stepper steps={steps} current={visualStep} />
      </div>
      {loading && <div className="appDialog__loading">Sending certificate...</div>}
      {warning && (
        <div className="sendCert__errorBanner" style={{ background: '#fffbeb', borderColor: '#fbbf24' }}>
          <i className="pi pi-exclamation-triangle sendCert__errorBanner__icon" style={{ color: '#d97706' }} />
          <div className="sendCert__errorBanner__body">
            <div className="sendCert__errorBanner__title" style={{ color: '#92400e' }}>Action required</div>
            <div className="sendCert__errorBanner__msg" style={{ color: '#78350f' }}>{warning}</div>
          </div>
          <button className="sendCert__errorBanner__dismiss" onClick={() => setWarning(null)} aria-label="Dismiss">
            <i className="pi pi-times" style={{ color: '#d97706' }} />
          </button>
        </div>
      )}
      {error && (
        <div className="sendCert__errorBanner">
          <i className="pi pi-exclamation-triangle sendCert__errorBanner__icon" />
          <div className="sendCert__errorBanner__body">
            <div className="sendCert__errorBanner__title">
              {error === '__server_error__'
                ? "It's not you — it's us"
                : error === '__bad_request__'
                ? 'Unable to send certificate'
                : 'Something went wrong'}
            </div>
            <div className="sendCert__errorBanner__msg">
              {error === '__server_error__'
                ? "We're having a temporary issue on our end. Please try again in a moment — we'll be back shortly."
                : error === '__bad_request__'
                ? 'Please confirm the recipient\'s email address is correct and all required fields are filled in, then try again.'
                : error === 'Unauthorized'
                ? 'Your session has expired. Please sign in again.'
                : error.startsWith('__')
                ? 'An unexpected error occurred. Please verify the email address is correct and try again.'
                : error}
            </div>
          </div>
          <button className="sendCert__errorBanner__dismiss" onClick={() => setError(null)} aria-label="Dismiss">
            <i className="pi pi-times" />
          </button>
        </div>
      )}
      <div className="sendCertPage__scroll">
        {step === 0 && (
          <Step1
            ref={step1Ref}
            email={email}
            sendToEmail={sendToEmail}
            ccToMe={ccToMe}
            copyToInsuredEmail={copyToInsuredEmail}
            certificateHolder={certificateHolder}
            setEmail={setEmail}
            setSendToEmail={setSendToEmail}
            setCcToMe={setCcToMe}
            setCopyToInsuredEmail={setCopyToInsuredEmail}
            setCertificateHolder={setCertificateHolder}
            insuredDatabaseId={insuredDatabaseId}
            insuredEmail={insuredEmail}
            setIsEmailValid={setIsEmailValid}
            fax={fax}
            setFax={setFax}
            insuredFax={insuredFax}
            setInsuredFax={setInsuredFax}
            onClientDetails={details => {
              setInsuredFirstName(details.firstName);
              setInsuredLastName(details.lastName);
              setInsuredCommercialName(details.commercialName);
              setInsuredState(details.stateName);
            }}
            selectedSentCertIds={selectedSentCertIds}
            setSelectedSentCertIds={setSelectedSentCertIds}
            sendToInsuredEmail={sendToInsuredEmail}
            setSendToInsuredEmail={setSendToInsuredEmail}
            sendToInsuredFax={sendToInsuredFax}
            setSendToInsuredFax={setSendToInsuredFax}
            sendToFax={sendToFax}
            setSendToFax={setSendToFax}
            onCoverPageSave={xml => setCoverPageOptions(xml)}
            selectedMasterCertificates={selectedMasterCertificates}
            setSelectedMasterCertificates={setSelectedMasterCertificates}
          />
        )}
        {step === 1 && (
          <Step2
            ref={step2Ref}
            selectedVehicles={selectedVehicles}
            setSelectedVehicles={setSelectedVehicles}
            selectedDrivers={selectedDrivers}
            setSelectedDrivers={setSelectedDrivers}
            selectedEquipment={selectedEquipment}
            setSelectedEquipment={setSelectedEquipment}
            selectedProperties={selectedProperties}
            setSelectedProperties={setSelectedProperties}
            insuredDatabaseId={insuredDatabaseId}
            masterCertPolicyIds={selectedMasterCertPolicyIds}
          />
        )}
        {step === 2 && (
          <Step4
            descTemplate={descTemplate}
            setDescTemplate={handleSetDescTemplate}
            descText={descText}
            setDescText={setDescText}
            descTemplateOptions={descTemplateOptions}
            attachedDescription={attachedDescription}
            descBodyLoading={descBodyLoading}
            showDescriptionInAcord101={showDescriptionInAcord101}
            setShowDescriptionInAcord101={setShowDescriptionInAcord101}
            onSearchTemplates={handleDescTemplateSearch}
            descTemplatesLoading={descTemplatesLoading}
          />
        )}
        {step === 3 && (
          <Step3
            emailSubject={emailSubject}
            setEmailSubject={handleEmailSubjectChange}
            emailTemplate={emailTemplate}
            setEmailTemplate={handleSetEmailTemplate}
            emailBody={emailBody}
            setEmailBody={setEmailBody}
            emailTemplateOptions={emailTemplateOptions}
            emailBodyLoading={emailBodyLoading}
            onSearchTemplates={handleEmailTemplateSearch}
            emailTemplatesLoading={emailTemplatesLoading}
          />
        )}
        {step === 4 && (
          <Step5
            combinePdf={combinePdf}
            setCombinePdf={setCombinePdf}
            insuredDatabaseId={insuredDatabaseId || ''}
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
            onUploadingChange={setIsFileUploading}
            masterCertificateId={selectedMasterCertificates[0]}
            masterCertificateName={rawMasterCertificates.find((m: any) => m.databaseId === selectedMasterCertificates[0])?.name}
            recipientEmail={email || undefined}
            sessionId={sessionId.current}
          />
        )}
        {step === 5 && (
          <Step7
            email={email}
            contactName={contactName}
            companyName={companyName}
            sendToEmail={sendToEmail}
            sendToInsuredEmail={sendToInsuredEmail}
            copyToInsuredEmail={copyToInsuredEmail}
            fax={fax}
            sendToFax={sendToFax}
            insuredFax={insuredFax}
            sendToInsuredFax={sendToInsuredFax}
            ccToMe={ccToMe}
            insuredDatabaseId={insuredDatabaseId}
            selectedMasterCertificates={selectedMasterCertificates}
            selectedCertificates={selectedCertificates}
            selectedVehicles={selectedVehicles}
            selectedDrivers={selectedDrivers}
            selectedEquipment={selectedEquipment}
            selectedProperties={selectedProperties}
            certificateHolder={certificateHolder}
            descText={descText}
            showDescriptionInAcord101={showDescriptionInAcord101}
            message={message}
            setMessage={setMessage}
            onSend={handleSendCertificate}
            isSending={loading}
            fileDatabaseId={uploadedFiles[0]?.fileId ?? null}
          />
        )}
      </div>
      {renderFooter()}
    </div>
  );
}
